import { randomUUID } from 'node:crypto';
import {
  ACTIVE_KEY,
  buildCursorRuntimeScript,
  CUE_GLOBAL,
  GLIDE_MAX_MS,
} from './cursor-runtime.js';

/**
 * A rectangle in MAIN-FRAME viewport coordinates.
 *
 * This is the space `Locator.boundingBox()` reports in — including for elements
 * nested inside iframes — which is exactly why the cue resolves boxes driver-side
 * rather than with `locator.evaluate`. See {@link AutonomousCursor}.
 */
export interface CueBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** The minimal surface a cue target (Locator or ElementHandle) must expose. */
export interface CueTarget {
  boundingBox(options?: { timeout?: number }): Promise<CueBox | null>;
}

/**
 * The minimal Playwright `Page` surface the cursor depends on. A real `Page`
 * satisfies this structurally; keeping it narrow lets tests provide a faithfully
 * typed fake without `any` casts or the full `Page` type.
 */
export interface CursorPage {
  addInitScript(script: { content: string }): Promise<unknown>;
  evaluate<R, Arg>(pageFunction: (arg: Arg) => R | Promise<R>, arg: Arg): Promise<R>;
  evaluate(pageFunction: string): Promise<unknown>;
  viewportSize?(): { width: number; height: number } | null;
}

/**
 * How long a rendered cue is held on screen before the real action is dispatched.
 *
 * Frame delivery is change-driven and throttled (`cdp-screencast.ts` drops any
 * frame arriving within `minIntervalMs` — 100ms for the recording subscriber), so
 * whether a short-lived visual state survives is phase-dependent. Holding for
 * ~2.5 throttle intervals makes an accepted frame containing the
 * on-target-but-not-yet-acted state very likely rather than a coin flip.
 *
 * This is NOT a claim that shorter states are impossible to capture — they are
 * merely unreliable, which is the actual defect being fixed.
 */
export const CUE_HOLD_MS = 250;

/**
 * How long the driver waits for `boundingBox()` before giving up on the cue.
 * Short on purpose: a missing or ambiguous locator must not add a meaningful wait
 * ahead of the real action, which does its own auto-waiting with a much longer
 * timeout and owns the resulting error message.
 */
const BBOX_TIMEOUT_MS = 800;

/**
 * Ceiling on any single cue round trip into the page. The runtime's API is frozen
 * and non-writable, but a page can still wedge a document; a cue is decoration and
 * must never be able to hang a run.
 */
const CUE_EVALUATE_TIMEOUT_MS = 2_000;

/**
 * Anticipatory, driver-cued in-page cursor for the fast-drive `run_code` path.
 *
 * Two signals, deliberately kept distinct so the recorded video cannot lie:
 *  - **Intent** — the driver calls `cueElement`/`cuePoint`/`cueKeys` BEFORE
 *    dispatching an action. That renders a cursor glide plus a neutral target
 *    halo, and returns the dwell the caller should wait so the state survives
 *    frame throttling.
 *  - **Execution** — the in-page reactive listeners (`cursor-runtime.ts`) draw the
 *    click ripple on the real `mousedown`. An action that never lands therefore
 *    never produces a click artifact; a pre-action ripple would assert "a click
 *    happened" about an action that may still time out on a covered or disabled
 *    element.
 *
 * **Never scrolls.** If `boundingBox()` returns null, the cue point falls outside
 * the viewport, or the viewport size is unknown, the cue is SKIPPED and today's
 * reactive-only behavior applies. Scrolling from the cue would fire the page's
 * scroll / lazy-load / virtualization side effects ahead of the action and would
 * violate the assumption that a cached snippet is self-sufficient from its entry
 * scroll position.
 *
 * The cue is a best-effort VISUAL HINT, not an actionability probe:
 * `boundingBox()` does not run Playwright's visible/stable/enabled/receives-events
 * checks, so a cue can land on an element the real action then waits on. That is
 * cosmetic; the real locator re-resolves independently and owns the outcome.
 *
 * Every page call is guarded — cursor wiring never blocks or breaks a run.
 */
export class AutonomousCursor {
  /** Master switch: install + reactive listeners (mirrors the driver overlay's gate). */
  private enabled: boolean;
  /**
   * Whether the driver may render anticipatory cues and spend dwell time.
   *
   * Gated on ACTUAL RECORDING state, not merely on the cursor being enabled —
   * `cursorOverlay: true` turns the cursor on without video (the canary-cli daemon
   * does exactly that in headed mode), and dwelling for frames nobody is encoding
   * is pure latency.
   */
  private cueEnabled: boolean | (() => boolean);
  /** The page the init script is registered on (page-keyed; re-installs on a new page). */
  private installedOn: CursorPage | null = null;
  /** Monotonic block counter; every `run_code` block gets its own generation. */
  private generation = 0;

  /**
   * Per-instance capability token. Freezing the in-page API stops a page
   * REPLACING it; this stops the page CALLING it — otherwise page script could
   * advance the generation and silently invalidate every real cue in the block.
   * It lives only here and in the init-script closure, which page code cannot
   * read.
   */
  private readonly token: string = randomUUID();

  /**
   * `cueEnabled` accepts a thunk on purpose. Recording state is not fixed at
   * construction — sessions can create or stop their encoder after connect, and
   * `videoEnabled` is reassigned at several points in `PlaywrightClient`. A
   * boolean captured once would go stale and either silently disable cues for a
   * session that later started recording, or keep dwelling after it stopped.
   */
  constructor(opts?: { enabled?: boolean; cueEnabled?: boolean | (() => boolean) }) {
    this.enabled = opts?.enabled ?? true;
    this.cueEnabled = opts?.cueEnabled ?? false;
  }

  /** Whether anticipatory cues are active (recording + cursor both on). */
  get cuesEnabled(): boolean {
    if (!this.enabled) return false;
    return typeof this.cueEnabled === 'function' ? this.cueEnabled() : this.cueEnabled;
  }

  /**
   * Register the in-page runtime: `addInitScript` (runs on every future document,
   * survives navigation) plus one immediate `evaluate` for the already-loaded
   * document. Page-keyed, so repeat calls on the same page don't accumulate.
   */
  async install(page: CursorPage): Promise<void> {
    if (!this.enabled) return;
    if (this.installedOn === page) return;
    const script = buildCursorRuntimeScript(this.token);
    try {
      await page.addInitScript({ content: script });
      this.installedOn = page;
    } catch {
      // Never block the run; a transient failure retries on the next run_code block.
      return;
    }
    try {
      await page.evaluate(script);
    } catch {
      // Never block the run.
    }
  }

  /** Toggle the reactive listeners (persisted in sessionStorage; survives navigation). */
  async setActive(page: CursorPage, on: boolean): Promise<void> {
    if (!this.enabled) return;
    try {
      await page.evaluate(
        ({ key, value }: { key: string; value: boolean }) => {
          try {
            sessionStorage.setItem(key, value ? '1' : '0');
          } catch {
            /* sessionStorage may be unavailable — animation just stays gated off */
          }
        },
        { key: ACTIVE_KEY, value: on }
      );
    } catch {
      // Never block the run.
    }
  }

  /**
   * Open a new cue generation for a `run_code` block, dropping anything the
   * previous block left on screen. Returns the generation for teardown pairing.
   */
  async beginBlock(page: CursorPage): Promise<number> {
    this.generation += 1;
    const gen = this.generation;
    if (!this.enabled) return gen;
    await this.callRuntime(page, 'beginGeneration', gen);
    return gen;
  }

  /**
   * Tear down a generation's transient visuals — halo, ripple, glow, keycaps — and
   * cancel their timers. Called on normal completion AND on abort, so an aborted
   * block can never leave a stale cue on screen. The cursor host itself is
   * installed once per document and deliberately survives.
   */
  async endBlock(page: CursorPage, gen: number): Promise<void> {
    if (!this.enabled) return;
    await this.callRuntime(page, 'endGeneration', gen);
  }

  /**
   * Cue an element-targeted action. Returns the milliseconds the caller should
   * wait before dispatching, or 0 when the cue was skipped (caller must NOT wait).
   */
  async cueElement(
    page: CursorPage,
    target: CueTarget,
    opts?: { position?: { x: number; y: number }; keys?: string[]; holdMs?: number }
  ): Promise<number> {
    if (!this.cuesEnabled) return 0;
    const box = await this.resolveBox(target);
    if (!box) return 0;
    const point = opts?.position
      ? { x: box.x + opts.position.x, y: box.y + opts.position.y }
      : { x: box.x + box.width / 2, y: box.y + box.height / 2 };
    if (!this.pointInViewport(page, point)) return 0;
    return this.render(page, { point, box, keys: opts?.keys, holdMs: opts?.holdMs });
  }

  /** Cue a coordinate action (`page.mouse.*`). Returns the dwell, or 0 if skipped. */
  async cuePoint(
    page: CursorPage,
    x: number,
    y: number,
    opts?: { holdMs?: number }
  ): Promise<number> {
    if (!this.cuesEnabled) return 0;
    if (!this.pointInViewport(page, { x, y })) return 0;
    return this.render(page, { point: { x, y }, holdMs: opts?.holdMs });
  }

  /**
   * Cue keyboard input: keycaps plus a glow on the focused control.
   *
   * Resolves focus in the TOP FRAME ONLY. When focus lives inside an iframe or a
   * closed shadow root, `document.activeElement` reports the container rather than
   * the real control, so the runtime returns no box and we render keycaps without
   * a misleading glow.
   */
  async cueKeys(page: CursorPage, keys: string[], opts?: { holdMs?: number }): Promise<number> {
    if (!this.cuesEnabled || keys.length === 0) return 0;
    const box = await this.focusBox(page);
    return this.render(page, { box: box ?? undefined, keys, holdMs: opts?.holdMs });
  }

  /**
   * Re-arm the current document's cue state without drawing anything.
   *
   * Called on every path where the cue is SKIPPED. A document created by a
   * navigation starts with no active latch, and across an origin change it has no
   * `sessionStorage` flag either — so without this, an action whose cue was
   * skipped (null box, offscreen target, or a legacy `page.click(selector)` that
   * is never cued at all) would also lose the REACTIVE ripple for the rest of the
   * block. Reuses `cue` with an empty request: it reasserts generation and active
   * before making any rendering decision.
   */
  async syncState(page: CursorPage): Promise<void> {
    if (!this.cuesEnabled) return;
    await this.render(page, { holdMs: 0 });
  }

  /** `boundingBox()` behind a short timeout; any failure means "skip the cue". */
  private async resolveBox(target: CueTarget): Promise<CueBox | null> {
    try {
      return await target.boundingBox({ timeout: BBOX_TIMEOUT_MS });
    } catch {
      // Strict-mode violation, detached element, timeout, closed page — all just
      // mean no cue. The real action re-resolves and owns the error.
      return null;
    }
  }

  /**
   * Viewport containment for the CUE POINT specifically, not the whole box — a
   * partially clipped element is still perfectly cueable as long as the point the
   * cursor travels to is on screen. Unknown viewport ⇒ skip rather than guess.
   */
  private pointInViewport(page: CursorPage, point: { x: number; y: number }): boolean {
    const viewport = page.viewportSize?.();
    if (!viewport || viewport.width <= 0 || viewport.height <= 0) return false;
    return point.x >= 0 && point.y >= 0 && point.x <= viewport.width && point.y <= viewport.height;
  }

  private async focusBox(page: CursorPage): Promise<CueBox | null> {
    try {
      return await page.evaluate((arg: { globalName: string; token: string }) => {
        const api = (window as unknown as Record<string, unknown>)[arg.globalName] as
          | { focusBox(t: string): { x: number; y: number; width: number; height: number } | null }
          | undefined;
        return api ? api.focusBox(arg.token) : null;
      }, { globalName: CUE_GLOBAL, token: this.token });
    } catch {
      return null;
    }
  }

  /**
   * Render the cue in the TOP FRAME and return the dwell to wait.
   *
   * Top-frame-only is the whole point of resolving the box driver-side: one cursor
   * instance in one coordinate system, so a target inside an iframe glides
   * continuously from wherever the cursor was instead of spawning a second cursor
   * in the child document with frame-relative coordinates.
   */
  private async render(
    page: CursorPage,
    req: { point?: { x: number; y: number }; box?: CueBox; keys?: string[]; holdMs?: number }
  ): Promise<number> {
    const holdMs = req.holdMs ?? CUE_HOLD_MS;
    try {
      const result = await this.withTimeout(page.evaluate(
        (arg: {
          globalName: string;
          token: string;
          req: {
            point?: { x: number; y: number };
            box?: CueBox;
            keys?: string[];
            holdMs: number;
            gen: number;
          };
        }) => {
          const api = (window as unknown as Record<string, unknown>)[arg.globalName] as
            | { cue(t: string, r: unknown): { glideMs: number } | null }
            | undefined;
          return api ? api.cue(arg.token, arg.req) : null;
        },
        { globalName: CUE_GLOBAL, token: this.token, req: { ...req, holdMs, gen: this.generation } }
      ));
      if (!result) return 0;
      // The page returns this number, so treat it as untrusted: a page that
      // replaced the (frozen, non-writable) API could otherwise pick the driver's
      // sleep duration. Clamp to the same bounds the runtime itself uses.
      const glide = Number(result.glideMs);
      if (!Number.isFinite(glide) || glide < 0) return holdMs;
      return Math.min(glide, GLIDE_MAX_MS) + holdMs;
    } catch {
      // Navigation or context destruction between boundingBox() and evaluate() is
      // a SKIP, never an action failure.
      return 0;
    }
  }

  /**
   * Bound a page call so a hijacked or never-settling evaluate cannot strand the
   * run. Resolves to null on timeout — the caller treats that as "no cue".
   */
  private async withTimeout<T>(p: Promise<T>): Promise<T | null> {
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      return await Promise.race([
        p,
        new Promise<null>((resolve) => {
          timer = setTimeout(() => resolve(null), CUE_EVALUATE_TIMEOUT_MS);
        }),
      ]);
    } finally {
      if (timer) clearTimeout(timer);
    }
  }

  /** Invoke a one-arg method on the in-page runtime. Best-effort. */
  private async callRuntime(page: CursorPage, method: string, gen: number): Promise<void> {
    try {
      await page.evaluate(
        (arg: { globalName: string; method: string; token: string; gen: number }) => {
          const api = (window as unknown as Record<string, unknown>)[arg.globalName] as
            | Record<string, ((t: string, g: number) => void) | undefined>
            | undefined;
          const fn = api?.[arg.method];
          if (typeof fn === 'function') fn(arg.token, arg.gen);
        },
        { globalName: CUE_GLOBAL, method, token: this.token, gen }
      );
    } catch {
      // Never block the run.
    }
  }
}
